import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, FileText, Trash2, Download, Search, FlaskConical, Filter } from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from './DictableTextarea';
import DatePickerField from '@/components/DatePickerField';

const TIPI = [
  { value: 'esami_ematici', label: 'Esami Ematici' },
  { value: 'radiografia', label: 'Radiografia' },
  { value: 'ecografia', label: 'Ecografia' },
  { value: 'tac', label: 'TAC' },
  { value: 'risonanza', label: 'Risonanza Magnetica' },
  { value: 'visita_specialistica', label: 'Visita Specialistica' },
  { value: 'elettrocardiogramma', label: 'ECG' },
  { value: 'spirometria', label: 'Spirometria' },
  { value: 'esame_urine', label: 'Esame Urine' },
  { value: 'esame_colturale', label: 'Esame Colturale' },
  { value: 'altro', label: 'Altro' },
];

const tipoBadgeColor = {
  esami_ematici: 'bg-red-100 text-red-700',
  radiografia: 'bg-blue-100 text-blue-700',
  ecografia: 'bg-cyan-100 text-cyan-700',
  tac: 'bg-purple-100 text-purple-700',
  risonanza: 'bg-indigo-100 text-indigo-700',
  visita_specialistica: 'bg-emerald-100 text-emerald-700',
  elettrocardiogramma: 'bg-amber-100 text-amber-700',
  spirometria: 'bg-teal-100 text-teal-700',
  esame_urine: 'bg-yellow-100 text-yellow-700',
  esame_colturale: 'bg-orange-100 text-orange-700',
  altro: 'bg-slate-100 text-slate-600',
};

export default function RefertiTab({ ospiteId }) {
  const { hasRole } = useAuth();
  const [referti, setReferti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('tutti');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    tipo: 'esami_ematici', titolo: '', data_referto: new Date().toISOString().slice(0, 10),
    medico_richiedente: '', laboratorio: '', note: '', contenuto_base64: '', nome_file: ''
  });

  const fetchReferti = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroTipo !== 'tutti') params.tipo = filtroTipo;
      const res = await api.get(`/ospiti/${ospiteId}/referti`, { params });
      setReferti(res.data);
    } catch { toast.error('Errore caricamento referti'); }
    setLoading(false);
  }, [ospiteId, filtroTipo]);

  useEffect(() => { fetchReferti(); }, [fetchReferti]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File troppo grande (max 10MB)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm(f => ({ ...f, contenuto_base64: ev.target.result.split(',')[1], nome_file: file.name }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.titolo) { toast.error('Inserisci il titolo del referto'); return; }
    try {
      await api.post('/referti', { ...form, ospite_id: ospiteId });
      toast.success('Referto caricato');
      setShowForm(false);
      setForm({ tipo: 'esami_ematici', titolo: '', data_referto: new Date().toISOString().slice(0, 10), medico_richiedente: '', laboratorio: '', note: '', contenuto_base64: '', nome_file: '' });
      fetchReferti();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleDownload = async (referto) => {
    try {
      if (referto.acquisizione_id) {
        const res = await api.get(`/acquisizione/${referto.acquisizione_id}/file`, { responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = referto.nome_file || `referto_${referto.id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      }
      const res = await api.get(`/referti/${referto.id}`);
      if (!res.data.contenuto_base64) { toast.error('Nessun file allegato'); return; }
      const byteChars = atob(res.data.contenuto_base64);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
      const blob = new Blob([byteArray]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.nome_file || `referto_${referto.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Errore download'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo referto?')) return;
    try { await api.delete(`/referti/${id}`); toast.success('Referto eliminato'); fetchReferti(); } catch { toast.error('Errore'); }
  };

  const filtered = referti.filter(r =>
    !search || r.titolo.toLowerCase().includes(search.toLowerCase()) ||
    r.tipo_label?.toLowerCase().includes(search.toLowerCase()) ||
    r.medico_richiedente?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="referti-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 flex-wrap">
          <div className="relative min-w-[180px] max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input placeholder="Cerca referto..." className="pl-9 h-9 text-sm" value={search} onChange={e => setSearch(e.target.value)} data-testid="search-referti" />
          </div>
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger className="w-48 h-9 text-xs" data-testid="filter-tipo-referto">
              <Filter className="w-3 h-3 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti i tipi</SelectItem>
              {TIPI.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {hasRole('coordinatore', 'medico', 'infermiere') && (
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-brand hover:bg-brand-dark text-white h-9" data-testid="add-referto-btn">
            <Plus className="w-4 h-4 mr-1" />Nuovo Referto
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="h-14 p-3" /></Card>)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12"><FlaskConical className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-400">Nessun referto{filtroTipo !== 'tutti' ? ' per questo tipo' : ''}</p></div>
      ) : (
        <div className="space-y-2">
          {filtered.map(r => (
            <Card key={r.id} className="hover:shadow-sm transition-shadow" data-testid={`referto-${r.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.titolo}</p>
                    <Badge className={`text-[10px] ${tipoBadgeColor[r.tipo] || tipoBadgeColor.altro}`}>{r.tipo_label}</Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                    <span>{new Date(r.data_referto).toLocaleDateString('it-IT')}</span>
                    {r.medico_richiedente && <span>{r.medico_richiedente}</span>}
                    {r.laboratorio && <span>{r.laboratorio}</span>}
                    {r.nome_file && <span className="text-blue-400">{r.nome_file}</span>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {r.nome_file && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownload(r)} title="Scarica"><Download className="w-3.5 h-3.5 text-blue-500" /></Button>
                  )}
                  {hasRole('coordinatore', 'medico') && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(r.id)} title="Elimina"><Trash2 className="w-3.5 h-3.5 text-slate-400" /></Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FlaskConical className="w-5 h-5 text-brand" />Nuovo Referto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Tipo *</Label>
                <Select value={form.tipo} onValueChange={v => setForm(f => ({ ...f, tipo: v }))}>
                  <SelectTrigger className="text-xs" data-testid="referto-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPI.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Data Referto *</Label>
                <DatePickerField value={form.data_referto} onChange={v => setForm(f => ({ ...f, data_referto: v }))} testId="referto-data" />
              </div>
            </div>
            <div><Label className="text-xs">Titolo / Descrizione *</Label>
              <Input value={form.titolo} onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))} placeholder="Es. Emocromo completo" data-testid="referto-titolo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Medico Richiedente</Label>
                <Input value={form.medico_richiedente} onChange={e => setForm(f => ({ ...f, medico_richiedente: e.target.value }))} placeholder="Dott. ..." />
              </div>
              <div><Label className="text-xs">Laboratorio / Struttura</Label>
                <Input value={form.laboratorio} onChange={e => setForm(f => ({ ...f, laboratorio: e.target.value }))} placeholder="Lab. Analisi ..." />
              </div>
            </div>
            <div><Label className="text-xs">Note</Label>
              <DictableTextarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} />
            </div>
            <div><Label className="text-xs">File Allegato (PDF, immagine - max 10MB)</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} className="text-xs" data-testid="referto-file" />
              {form.nome_file && <p className="text-xs text-emerald-600 mt-1">File: {form.nome_file}</p>}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button onClick={handleSave} className="bg-brand hover:bg-brand-dark text-white" data-testid="save-referto">Carica Referto</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
